import { validateHTMLColorHex } from 'validate-color';
import { extend } from 'vee-validate';
import { required, email, min_value, numeric, max_value, integer } from 'vee-validate/dist/rules';
import { isValidPhoneNumber } from 'libphonenumber-js/min';
import i18n from '@/i18n';
import { state as authState } from '@/store/auth/auth';
import { getCountryFromAddress } from '@/utils/currency';

extend('required', {
	...required,
	message: i18n.t('shared.validator.required')
});

extend('integer', {
	...integer,
	message: i18n.t('shared.validator.integer')
});

extend('email', {
	...email,
	message: i18n.t('shared.validator.email')
});

extend('min', {
	validate(value, args) {
		return value.length >= args.length;
	},
	params: ['length'],
	message: i18n.t('shared.validator.min')
});

extend('max', {
	validate(value, args) {
		return value.length <= args.length;
	},
	params: ['length'],
	message: i18n.t('shared.validator.max')
});

extend('min_value', {
	...min_value,
	message: i18n.t('shared.validator.min_value')
});

extend('max_value', {
	...max_value,
	message: i18n.t('shared.validator.max_value')
});

extend('numeric', {
	...numeric,
	message: i18n.t('shared.validator.numeric')
});

extend('decimal', {
	params: ['decimals', 'separator'],
	// TO DO: FIX ts error
	//@ts-ignore
	validate: (value, { decimals = '*', separator = '.' } = {}) => {
		if (value === null || value === undefined || value === '') {
			return {
				valid: false
			};
		}
		if (Number(decimals) === 0) {
			return {
				valid: /^-?\d*$/.test(value)
			};
		}
		const regexPart = decimals === '*' ? '+' : `{1,${decimals}}`;
		const regex = new RegExp(`^[-+]?\\d*(\\${separator}\\d${regexPart})?([eE]{1}[-]?\\d+)?$`);

		return {
			valid: regex.test(value)
		};
	},
	message: (field, params) => {
		if (!params.decimals) {
			return i18n.t('shared.validator.decimal');
		}
		return i18n.t('shared.validator.decimal_with_limit', { decimals: params.decimals });
	}
});

extend('validateColor',{
	validate: (color) => {
		return color
			? validateHTMLColorHex(color)
			: true;
	},
	message: i18n.t('shared.validator.invalid_color')
});

extend('higherThanZero',{
	validate: (value) => {
		return value > 0;
	},
	message: i18n.t('shared.validator.higher_than_zero')
});

extend('greater_than', {
	params: ['target', 'field1-name', 'field2-name'],
	// TO DO: FIX ts error
	//@ts-ignore
	validate(value, { target }) {
		return value > target;
	},
	message: i18n.t('shared.validator.greater_than', { field1: '{field1-name}', field2: '{field2-name}' })
});

extend('characters_for_name', {
	validate: (value) => {
		if (value[value.length - 1] === '-' || value[value.length - 1] === '\'') {
			return false;
		}

		const regex = /(?![\'-])^[A-ZÀÂÆÇÉÈÊËÏÎÔŒÙÛÜßŸ\'-\s]*$/i;
		return {
			valid: regex.test(value)
		};
	},
	message: i18n.t('shared.validator.characters_for_name')
});

extend('uppercase', {
	validate: (value) => value === value.toUpperCase(),
	message: i18n.t('shared.validator.uppercase')
});

extend('uniqueName', {
	// TO DO: FIX ts error
	//@ts-ignore
	validate(value, { list }) {
		if (!Array.isArray(list)) {
			return true;
		}
		return list.filter(name => name === value).length === 1;
	},
	params: ['list'],
	message: i18n.t('shared.validator.name_must_be_unique')
});

extend('https_url', {
	validate: (value: string) => {
		if (!value) {
			return true;
		}
		try {
			const url = new URL(value);
			return url.protocol === 'https:';
		}
		catch {
			return false;
		}
	},
	message: i18n.t('shared.validator.url_must_be_secure')
});

extend('valid_regex', {
	validate: (value: string) => {
		if (!value) {
			return true;
		}
		try {
			new RegExp(value);
			return true;
		}
		catch {
			return false;
		}
	},
	message: i18n.t('shared.validator.valid_regex')
});

extend('contains_lowercase', {
	validate: (value: string) => /[a-z]/.test(value),
	message: i18n.t('shared.validator.contains_lowercase')
});

extend('contains_uppercase', {
	validate: (value: string) => /[A-Z]/.test(value),
	message: i18n.t('shared.validator.contains_uppercase')
});

extend('contains_number', {
	validate: (value: string) => /[0-9]/.test(value),
	message: i18n.t('shared.validator.contains_number')
});

extend('contains_special_char', {
	validate: (value: string) => /[!@#$%^&*?]/.test(value),
	message: i18n.t('shared.validator.contains_special_char')
});

extend('phone', {
	validate: (value: string) => {
		if (!value) {
			return true;
		}

		const digits = value.replace(/\D/g, '');

		// International format with + prefix (E.164)
		if (value.startsWith('+')) {
			return isValidPhoneNumber(value);
		}

		// UK national format when the location is in the UK (starts with 0, 10-11 digits)
		if (getCountryFromAddress(authState.restaurant?.address) === 'GB') {
			return /^0\d{9,10}$/.test(digits);
		}

		// North American format (10-11 digits)
		return digits.length >= 10 && digits.length <= 11;
	},
	message: i18n.t('shared.validator.phone')
});